/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { ReactNode } from 'react'

import { DetailSheet } from '@/components/ui/detail-sheet/DetailSheet'
import { useAuth } from '@/hooks'

import { ZOrderPicker } from './editor/ZOrderPicker'
import { type GeofenceCategory, type ManagedGeofence } from './types'

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="py-3 text-sm">
    <dt className="font-semibold text-base-content/80">{label}</dt>
    <dd className="mt-1 min-w-0 pl-3">{children}</dd>
  </div>
)

interface GeofenceDetailSheetProps {
  geofence: ManagedGeofence | undefined
  categories: Map<number, GeofenceCategory>
  /** Editing needs a desktop-sized screen. */
  canEdit: boolean
  onEdit: () => void
  onDelete: () => void
  onHistory: () => void
}

/** The open geofence: what it is, where, how big, and the way to its edit history. */
export const GeofenceDetailSheet = ({
  geofence,
  categories,
  canEdit,
  onEdit,
  onDelete,
  onHistory,
}: GeofenceDetailSheetProps) => {
  const { hasPermission } = useAuth()
  if (!geofence) return null

  const geofenceCategories = geofence.categoryIds
    .map((id) => categories.get(id))
    .filter((c): c is GeofenceCategory => !!c)
  const header = (
    <span className="block w-full min-w-0 truncate text-sm font-semibold" title={geofence.name}>
      {geofence.name}
    </span>
  )

  const footer = (
    <div className="flex w-full items-center justify-between gap-2">
      <button type="button" className="btn btn-sm btn-ghost text-error" onClick={onDelete} aria-label="Delete geofence">
        Delete
      </button>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={onEdit}
          disabled={!canEdit}
          title={canEdit ? 'Edit geofence' : 'Editing geofences needs a larger screen'}
        >
          Edit
        </button>
      </div>
    </div>
  )

  return (
    <DetailSheet
      isOpen
      variant="pane"
      header={header}
      headerHeight="table"
      bodyKey={geofence.id}
      footer={footer}
      footerBorder={false}
    >
      <dl>
        <Row label="Category">
          {geofenceCategories.length > 0 ? (
            <ul className="list-disc pl-6">
              {geofenceCategories.map((category) => (
                <li key={category.id}>{category.name}</li>
              ))}
            </ul>
          ) : (
            <span className="text-base-content/50">Not categorised</span>
          )}
        </Row>
        <Row label="Display Layout">
          <div className="space-y-1">
            <div>Z-order: {geofence.zorder}</div>
            <div>
              <ZOrderPicker value={geofence.zorder} readOnly />
            </div>
          </div>
        </Row>
        <div className="flex items-center gap-2 py-3 text-sm">
          <span className="font-semibold text-base-content/80">Shown on maps</span>
          <input
            type="checkbox"
            className="checkbox checkbox-xs"
            checked={geofence.visible}
            readOnly
            aria-label="Shown on maps"
          />
        </div>
      </dl>
      {hasPermission('auditLogs') && (
        <div className="mt-1">
          <button type="button" className="text-xs text-primary hover:text-primary/80" onClick={onHistory}>
            View Change History
          </button>
        </div>
      )}
    </DetailSheet>
  )
}
